import { useCallback, useMemo, useRef, useState } from 'react';
import { ScenarioSelector } from './components/ScenarioSelector';
import { Timeline } from './components/Timeline';
import { ResultSummary } from './components/ResultSummary';
import { ExportButton } from './components/ExportButton';
import { PrivacyNote } from './components/PrivacyNote';
import { HowItWorks } from './components/HowItWorks';
import {
  ApiError,
  CancelledError,
  cancelledRunOutput,
  executeScenario,
  replayFailedEvent,
  type ScenarioRunStep,
} from './lib/api';
import type { RunStatus, ScenarioId } from './domain/types';

type RunPhase = 'idle' | 'running' | 'done';

export default function App() {
  const [scenarioId, setScenarioId] = useState<ScenarioId>('happy-path');
  const [phase, setPhase] = useState<RunPhase>('idle');
  const [steps, setSteps] = useState<ScenarioRunStep[]>([]);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const lastStep = steps.length > 0 ? steps[steps.length - 1] : null;
  const lastStatus: RunStatus | null = lastStep ? lastStep.output.status : null;
  const canReplay = lastStatus === 'failed' || lastStatus === 'partial';

  const runningLabel = useMemo(() => {
    if (phase !== 'running') return null;
    return `Ejecutando escenario "${scenarioId}"…`;
  }, [phase, scenarioId]);

  const execute = useCallback(async () => {
    setPhase('running');
    setError(null);
    setSteps([]);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const results = await executeScenario(scenarioId, controller.signal);
      setSteps(results);
    } catch (err) {
      if (err instanceof CancelledError) {
        setSteps((prev) => [
          ...prev,
          {
            label: 'Cancelado por el visitante',
            eventId: '',
            output: cancelledRunOutput(scenarioId),
          },
        ]);
      } else if (err instanceof ApiError) {
        setError(
          `No pudimos procesar esta entrada. Tus datos no se enviaron; corrige los campos señalados. (${err.code}: ${err.message})`,
        );
      } else {
        setError(
          'No pudimos procesar esta entrada. Tus datos no se enviaron; corrige los campos señalados.',
        );
      }
    } finally {
      setPhase('done');
      abortRef.current = null;
    }
  }, [scenarioId]);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const replay = useCallback(async () => {
    if (!lastStep?.eventId) return;
    setPhase('running');
    setError(null);
    try {
      const output = await replayFailedEvent(lastStep.eventId);
      setSteps((prev) => [
        ...prev,
        { label: 'Reproducción del evento fallido', eventId: lastStep.eventId, output },
      ]);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(`No se pudo reproducir el evento. (${err.code}: ${err.message})`);
      } else {
        setError('No se pudo reproducir el evento.');
      }
    } finally {
      setPhase('done');
    }
  }, [lastStep]);

  return (
    <>
      <a className="skip-link" href="#main-content">
        Saltar al contenido principal
      </a>
      <div className="app-shell">
        <header className="hero">
          <p className="hero__eyebrow">Privacidad: 100% sintético</p>
          <h1>Haz visible lo que normalmente falla en silencio.</h1>
          <p>
            Elige un escenario de fallo de webhook y observa recepción, validación, deduplicación,
            reintento y resultado en una línea temporal determinista.
          </p>
        </header>

        <PrivacyNote />

        <main id="main-content" className="workbench">
          <div className="panel">
            <h2>Entrada y escenario</h2>
            <ScenarioSelector
              value={scenarioId}
              onChange={setScenarioId}
              disabled={phase === 'running'}
            />
            <div className="button-row">
              <button
                type="button"
                className="button button--primary"
                onClick={() => void execute()}
                disabled={phase === 'running'}
              >
                Ejecutar escenario
              </button>
              {phase === 'running' ? (
                <button type="button" className="button button--secondary" onClick={cancel}>
                  Cancelar
                </button>
              ) : null}
              {canReplay ? (
                <button
                  type="button"
                  className="button button--secondary"
                  onClick={() => void replay()}
                  disabled={phase === 'running'}
                >
                  Reproducir evento fallido
                </button>
              ) : null}
            </div>
            <p className="status-line" role="status" aria-live="polite">
              {runningLabel ??
                (phase === 'done'
                  ? 'Ejecución terminada. Abre cada decisión para revisar su evidencia.'
                  : '')}
            </p>
            {error ? (
              <p className="status-line" role="alert" style={{ color: 'var(--color-critical)' }}>
                {error}
              </p>
            ) : null}
          </div>

          <div className="panel">
            <h2>Resultado y explicación</h2>
            <ResultSummary status={lastStatus} summary={lastStep?.output.summary ?? null} />
            <Timeline steps={steps} />
            <div className="button-row">
              <ExportButton scenarioId={scenarioId} steps={steps} />
            </div>
          </div>
        </main>

        <HowItWorks />

        <footer className="site-footer">
          <p>
            Webhook Reliability Playground · Cloudflare Pages + Worker + D1 · sin datos reales, TTL
            de 7 días para toda ejecución sintética.
          </p>
        </footer>
      </div>
    </>
  );
}
